/**
 * Game Winners Composable
 *
 * This composable provides functionality to fetch and manage data about game wins across
 * the platform. It handles two main types of win data:
 * 1. Top Wins - Highest value wins across all games
 * 2. Recent Wins - Latest chronological wins across all games
 *
 * The composable manages the API requests, loading states, and data transformation
 * to provide ready-to-use data for UI components.
 *
 * @see {@link https://cactuscorp.atlassian.net/wiki/x/BgAbEw | Complete Documentation and Use}.
 *
 * @returns {Object} Return object containing all state variables and methods for game wins management
 *
 * @property {Ref<TopWin[]>} `gamesTopWins` - Reactive ref containing sorted list of highest win records
 * @property {Ref<boolean>} `isLoadingTopWins` - Reactive ref tracking the state of top wins data fetching
 * @property {Ref<string|null>} `topWinsError` - Error message for failures in the top wins API request
 * @property {Function} `fetchTopWins` - Method to retrieve highest win records across all games
 * @property {Ref<LastWin[]>} `gamesLastWins` - Reactive ref containing chronologically ordered recent win records
 * @property {Ref<boolean>} `isLoadingLastWins` - Reactive ref tracking the state of recent wins data fetching
 * @property {Ref<string|null>} `lastWinsError` - Error message for failures in the recent wins API request
 * @property {Function} `fetchLastWins` - Method to retrieve most recent win records
 * @property {Function} `validateGameUrl` - Method to validate and normalize game redirect URLs
 *
 * @example
 * // In a Vue component
 * const {
 *   gamesTopWins,
 *   isLoadingTopWins,
 *   fetchTopWins,
 *   gamesLastWins,
 *   isLoadingLastWins,
 *   fetchLastWins,
 *   validateGameUrl
 * } = useGamesWinners();
 *
 * // Fetch top and recent wins
 * onMounted(async () => {
 *   await Promise.all([fetchTopWins(), fetchLastWins()]);
 * });
 *
 * // Validate game URL before navigation
 * const gameRedirectUrl = validateGameUrl('/casino/poker'); // Returns: '/games/poker'
 */
export function useGamesWinners() {
  const { ApiService } = useApiService();
  const { topWins, lastWins } = storeToRefs(useGamesWinnersStore());

  // Top wins
  const isLoadingTopWins = ref(false);
  const topWinsError = ref<string | null>(null);

  // Recent wins
  const isLoadingLastWins = ref(false);
  const lastWinsError = ref<string | null>(null);

  /**
   * Generates responsive image srcset for game thumbnails
   *
   * This helper function normalizes thumbnail paths and creates appropriate
   * srcset values for responsive images across different screen sizes.
   *
   * @param {string} thumbnail - Raw thumbnail path from the API
   * @returns {string} Formatted srcset string for responsive images
   * @private
   */
  const imageSrcSet = (thumbnail: string) => {
    if (!thumbnail || typeof thumbnail !== 'string') {
      return '';
    }

    const normalizedThumbnail = thumbnail
      .replace('/public', '')
      .replace('/mobile', '')
      .replace('/ipad', '')
      .trim();

    // Validate that we have a valid URL path
    if (!normalizedThumbnail || normalizedThumbnail.length < 2) {
      return '';
    }

    return `${normalizedThumbnail}/mobile 200w, ${normalizedThumbnail}/ipad 600w`;
  };

  /**
   * Validates and normalizes game redirect URL
   *
   * This function processes game URLs to ensure they follow the correct routing pattern:
   * - URLs starting with /games are returned as-is
   * - URLs starting with /casino have /casino replaced with /games
   * - All other URLs get /games prepended
   *
   * @param {string} gameUrl - Raw game URL to be validated
   * @returns {string} Normalized game URL with proper /games prefix
   *
   * @example
   * validateGameUrl('/games/slot-game') // Returns: '/games/slot-game'
   * validateGameUrl('/casino/poker')    // Returns: '/games/poker'
   * validateGameUrl('/some-game')       // Returns: '/games/some-game'
   */
  const validateGameUrl = (gameUrl: string): string => {
    // Normaliza para lowercase
    const normalizedUrl = gameUrl.toLowerCase();

    if (normalizedUrl.startsWith('/games')) {
      return normalizedUrl;
    }

    if (normalizedUrl.startsWith('/casino')) {
      return normalizedUrl.replace('/casino', '/games');
    }

    return `/games/${normalizedUrl}`;
  };

  /**
   * Fetches highest win records across all games
   *
   * Retrieves a collection of top win records from the API, enhances them
   * with responsive image srcsets, and stores them in the reactive state.
   *
   * @returns {Promise<TopWin[]>} Promise resolving to an array of enhanced top win records
   * @throws {Error} If the API request fails, the error is caught, stored in topWinsError, and re-thrown
   *
   * @example
   * const { fetchTopWins } = useGamesWinners();
   * try {
   *   const topWinRecords = await fetchTopWins();
   *   // Display top win records in the UI
   * } catch (error) {
   *   // Handle error
   * }
   */
  const fetchTopWins = async () => {
    isLoadingTopWins.value = true;
    topWinsError.value = null;

    try {
      const response = await ApiService.get<TopWin[]>('/bff/games/top-wins');

      const responseData = response.data.map((item: any) => ({
        ...item,
        player_name: capitalizeWords(item.player_name),
        game_image_src_set: imageSrcSet(item.game_image),
        game_slug: validateGameUrl(item.game_slug),
      }));

      if (responseData.length > 0) {
        topWins.value = responseData;
      }

      return responseData;
    } catch (err: any) {
      topWinsError.value = err.message || 'Error fetching top wins';
      throw err;
    } finally {
      isLoadingTopWins.value = false;
    }
  };

  /**
   * Fetches most recent win records across all games
   *
   * Retrieves a collection of the latest win records from the API, enhances them
   * with responsive image srcsets, and stores them in the reactive state.
   *
   * @returns {Promise<LastWin[]|null>} Promise resolving to an array of enhanced recent win records,
   *                                   or null if the request fails
   *
   * @example
   * const { fetchLastWins } = useGamesWinners();
   * const recentWins = await fetchLastWins();
   * if (recentWins) {
   *   // Display recent win activity in the UI
   * } else {
   *   // Handle error case
   * }
   */
  const fetchLastWins = async () => {
    isLoadingLastWins.value = true;
    lastWinsError.value = null;

    try {
      const response = await ApiService.get<LastWin[]>('/bff/games/last-wins');

      const responseData = response.data.map((item: any) => ({
        ...item,
        game_image_src_set: imageSrcSet(item.game_image),
        game_slug: validateGameUrl(item.game_slug),
      }));

      if (responseData.length > 0) {
        lastWins.value = responseData;
      }

      return responseData;
    } catch (e: any) {
      lastWinsError.value = e.message || 'Error fetching recent wins';

      return null;
    } finally {
      isLoadingLastWins.value = false;
    }
  };

  return {
    gamesTopWins: topWins,
    gamesLastWins: lastWins,
    isLoadingTopWins,
    isLoadingLastWins,
    topWinsError,
    lastWinsError,
    fetchTopWins,
    fetchLastWins,
    validateGameUrl,
  };
}
